/* @flow */
// typecheck: ignore file
import ConfirmDialog from 'components/ConfirmDialog.jsx';
import CurrentUser from 'auth/CurrentUser';
import FlexibleBlock from 'components/FlexibleBlock.jsx';
import Group from 'entities/Group';
import GroupName from 'groups/components/GroupName.jsx';
import GroupAvatar from 'groups/components/GroupAvatar.jsx';
import Heading from 'components/Heading.jsx';
import InviteInClassInstructionsTab from 'groups/components/InviteInClassInstructionsTab.jsx';
import InviteManualAddTab from 'groups/components/InviteManualAddTab.jsx';
import InvitePDFTab from 'groups/components/InvitePDFTab.jsx';
import InviteShareLinkTab from 'groups/components/InviteShareLinkTab.jsx';
import OrganizationStore from 'stores/OrganizationStore';
import PromptActions from 'actions/PromptActions';
import { PromptNames } from 'constants/PromptConstants';
import PromptStore from 'stores/PromptStore';
import React from 'react';
import TeacherAddUploadTab from 'groups/components/TeacherAddUploadTab.jsx';
import Text from 'components/Text.jsx';
import LeftTabModal from 'components/LeftTabModal.jsx';
import LeftTabModalTitle from 'components/LeftTabModalTitle.jsx';
import { mountModal } from 'lib/modal';
import shouldEnableCsvUploadForGroup from 'utils/shouldEnableCsvUploadForGroup';

import './styles/GroupInviteModal.scss';

const EVENT_SCREEN_NAME = 'GroupInviteModal';

class GroupInviteModal extends React.Component {
  props: {
    group: Group,
    initialTab?: ?string,
    onRequestHide?: () => any,
    hideOnBlur?: bool,
  };

  static TABS = ['CSV_UPLOAD', 'INVITE', 'PDF', 'INSTRUCTIONS', 'LINK']
    .reduce((obj, elem) => {
      obj[elem] = elem;
      return obj;
    }, {});

  static defaultProps = {
    onRequestHide: () => {},
    hideOnBlur: false,
  };

  static childContextTypes = {
    eventScreenName: React.PropTypes.string,
  };

  state = {
    activeTab: this.props.initialTab ?
      this.getEnabledTabs()[this.props.initialTab] :
      this.getDefaultTab(),
  };

  getChildContext() {
    return {
      eventScreenName: EVENT_SCREEN_NAME,
    };
  }

  getDefaultTab(): number {
    const tabs = this.getEnabledTabs();
    if (GroupInviteModal.TABS.CSV_UPLOAD in tabs) {
      return tabs.CSV_UPLOAD;
    } else if (GroupInviteModal.TABS.INVITE in tabs) {
      return tabs.INVITE;
    } else {
      return tabs.INSTRUCTIONS;
    }
  }

  getEnabledTabs(): Object {
    const tabs = {};
    let tabIndex = 0;
    if (this.shouldEnableCSVUpload()) {
      tabs.CSV_UPLOAD = tabIndex++;
    }
    if (CurrentUser.getUser().hasManualInviteSupported()) {
      tabs.INVITE = tabIndex++;
      tabs.PDF = tabIndex++;
      tabs.INSTRUCTIONS = tabIndex++;
      tabs.LINK = tabIndex++;
    } else {
      tabs.INSTRUCTIONS = tabIndex++;
    }
    return tabs;
  }

  handleClick() {
    if (!PromptStore.hasDismissed(PromptNames.INVITE_DIALOG_CONFIRM_HIDE)) {
      PromptActions.recordDismissal(PromptNames.INVITE_DIALOG_CONFIRM_HIDE);
    }
  }

  handleHideRequest = () => {
    const showConfirm = (
      this.props.group.getMembershipsCount() <= 1
      && !PromptStore.hasDismissed(PromptNames.INVITE_DIALOG_CONFIRM_HIDE)
    );
    if (showConfirm) {
      mountModal(
        <ConfirmDialog
          eventScreenName="GroupInviteModal"
          title={'Would you like to add students and parents to your class?'}
          onCancel={this.props.onRequestHide}
          actionButtonText={'Add'}
          cancelButtonText={'Cancel'}
          hideCloseButton
          hideOnBlur={false}
        />, this);
      PromptActions.recordDismissal(PromptNames.INVITE_DIALOG_CONFIRM_HIDE);
    } else if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  }

  setActiveTab = (index: number) => {
    this.setState({ activeTab: index });
  };

  shouldEnableCSVUpload() {
    const { group } = this.props;
    return shouldEnableCsvUploadForGroup(group);
  }

  smsEnabled(): boolean {
    return this.props.group.smsEnabledForUser(CurrentUser.getUser());
  }

  renderSidebarHeader() {
    const { group } = this.props;
    return (
      <div>
        <FlexibleBlock className="GroupInviteModal-group" flex="right">
          <div>
            <GroupAvatar group={group} className="GroupInviteModal-group-avatar" />
          </div>
          <div>
            <h2 className="GroupInviteModal-group-name">
              <Text truncate>
                <GroupName group={group} />
              </Text>
            </h2>
            <div className="GroupInviteModal-group-code">@{group.getCode()}</div>
          </div>
        </FlexibleBlock>
        <Heading flush level="3">Ways to add people</Heading>
      </div>
    );
  }

  renderInClassInstructionsTab() {
    const { group } = this.props;
    const smsEnabled = this.smsEnabled();

    const title = (
      <LeftTabModalTitle
        glyphName={smsEnabled ? 'display-board' : 'mobile'}
        glyphColor="violet"
        title={smsEnabled ? 'In-person instructions' : 'QR code'}
        titleShort={smsEnabled ? 'Instruct' : 'QR code'}
      />
    );

    return (
      <InviteInClassInstructionsTab
        group={group}
        title={title}
        key="inClassInstructionsTab"
      />
    );
  }

  renderShareLinkTab() {
    const { group } = this.props;
    const title = (
      <LeftTabModalTitle
        glyphName="link"
        glyphColor="flame"
        title="Share a link"
        titleShort="Link"
      />
    );
    return (
      <InviteShareLinkTab
        group={group}
        title={title}
        key="shareLinkTab"
      />
    );
  }

  renderPDFTab() {
    const { group } = this.props;
    const title = (
      <LeftTabModalTitle
        glyphName="printer"
        glyphColor="lava"
        title="Printable PDFs"
        titleShort="PDF"
      />
    );
    return (
      <InvitePDFTab
        group={group}
        title={title}
        key="pdfTab"
      />
    );
  }

  renderManualInviteTab() {
    const smsEnabled = this.smsEnabled();
    const title = (
      <LeftTabModalTitle
        glyphName="avatar-add"
        glyphColor="shamrock"
        title={smsEnabled ?
          'Phone or email contacts' :
          'Email invitation'}
        titleShort="Invite"
      />
    );
    return (
      <InviteManualAddTab group={this.props.group} smsEnabled={smsEnabled} title={title} key="manualAddTab" />
    );
  }

  renderCSVUploadTab() {
    if (!this.shouldEnableCSVUpload()) {
      return null;
    }

    const { group } = this.props;
    const org = OrganizationStore.getOrganization(group.getStaffClassOrgId());
    const title = (
      <LeftTabModalTitle
        glyphName="spreadsheet"
        glyphColor="midnight"
        title="Upload a spreadsheet"
        titleShort="Upload"
      />);

    return (
      <TeacherAddUploadTab
        key="addUploadTab"
        group={group}
        organization={org}
        ref="uploadTab"
        title={title}
      />
    );
  }

  render() {
    let body;

    if (CurrentUser.getUser().hasManualInviteSupported()) {
      body = [
        this.renderCSVUploadTab(),
        this.renderManualInviteTab(),
        this.renderPDFTab(),
        this.renderInClassInstructionsTab(),
        this.renderShareLinkTab(),
      ];
    } else {
      body = [
        this.renderCSVUploadTab(),
        this.renderInClassInstructionsTab(),
      ];
    }

    return (
      <LeftTabModal
        eventScreenName="GroupInviteModal"
        onRequestHide={this.props.onRequestHide}
        sidebarHeader={this.renderSidebarHeader()}
        onTabChange={this.setActiveTab}
        onMaskHide={this.handleHideRequest}
        onDialogClick={this.handleClick}
        hideOnBlur={this.props.hideOnBlur}
        tabActive={this.state.activeTab}>
        {body}
      </LeftTabModal>
    );
  }
}

export default GroupInviteModal;



// WEBPACK FOOTER //
// ./src/groups/components/GroupInviteModal.jsx